<template>
  <div>
    <div class="new-main-page">
      <h1 class="new-main-header">
        <img
          :src="theme.current.headerImage"
          alt="AI Logo"
          class="header-image"
        />
      </h1>
      <div class="config-shell">
        <nav class="config-nav">
          <div v-for="section in SECTIONS" :key="section.key" class="config-group">
            <span class="config-group-label">{{ section.label }}</span>
            <div class="config-group-tabs">
              <button
                v-for="t in section.tabs"
                :key="t.key"
                :class="{ active: tab === t.key }"
                @click="tab = t.key"
              >
                {{ t.label }}
              </button>
            </div>
          </div>
        </nav>
        <!-- All panels stay mounted so switching tabs never discards an
             in-progress edit, a chosen comparison, or a half-filled schedule
             form; the lazy ones fetch on first activation instead. -->
        <ModelConfigPanel v-show="tab === 'edit'" />
        <ModelDiffPanel v-show="tab === 'compare'" :active="tab === 'compare'" />
        <SessionCadencePanel
          v-show="tab === 'cadence'"
          :active="tab === 'cadence'"
        />
        <ReminderSchedulePanel
          v-show="tab === 'reminders'"
          :active="tab === 'reminders'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useThemeStore } from "@/stores/theme";
import ModelConfigPanel from "./ModelConfigPanel.vue";
import ModelDiffPanel from "./ModelDiffPanel.vue";
import SessionCadencePanel from "./SessionCadencePanel.vue";
import ReminderSchedulePanel from "./ReminderSchedulePanel.vue";

// Configuration areas, grouped so this page can host more than chat-model
// prompts. Adding an area means appending a section here and rendering its
// panel alongside the others below; nothing else on the page changes.
const SECTIONS = [
  {
    key: "chat-model",
    label: "Chat model",
    tabs: [
      { key: "edit", label: "Edit" },
      { key: "compare", label: "Compare" },
    ],
  },
  {
    key: "sessions",
    label: "Sessions",
    tabs: [
      { key: "cadence", label: "Cadence" },
      { key: "reminders", label: "Reminders" },
    ],
  },
];

export default {
  name: "ConfigurationPage",
  components: {
    ModelConfigPanel,
    ModelDiffPanel,
    SessionCadencePanel,
    ReminderSchedulePanel,
  },
  data() {
    return { SECTIONS, tab: "edit" };
  },
  setup() {
    const theme = useThemeStore();
    return { theme };
  },
};
</script>

<style scoped>
.new-main-page {
  display: flex;
  flex-direction: column;
  height: var(--app-height);
  min-height: 100vh;
  min-height: -webkit-fill-available;
}

.new-main-page h1,
.new-main-page p,
.new-main-page form,
.new-main-page div {
  width: 100%;
  text-align: left;
}

.new-main-header {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 150px; /* Adjust as needed */
  padding: 0;
  margin: 0;
}

/* The shell owns the horizontal inset for everything on the page, so the tab
   strip and both panels line up on the left and keep the same gap from the
   window on the right. Panels below carry no side margins of their own. */
.new-main-page .config-shell {
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px;
}

.new-main-page .config-nav {
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  align-items: flex-end;
  width: auto;
  margin: 12px 0 0;
}

/* No gap: the label bar sits flush on its tabs so the two read as one
   container rather than as a caption that happens to be nearby. */
.new-main-page .config-group {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: auto;
}

/* Bridge spanning the group's tabs. Rounded on top and square along the
   bottom, so it caps the group and hands off cleanly to the tabs beneath --
   which give up their own top rounding for the same reason. */
.config-group-label {
  display: block;
  text-align: center;
  font-size: 0.68em;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #5c6570;
  background: #dfe3e7;
  border: 1px solid #d6d6d6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  padding: 5px 14px 4px;
}

/* Tabs butt together into one strip under the bridge, rather than floating
   apart, which would let the page show through the group the bridge just drew. */
.new-main-page .config-group-tabs {
  display: flex;
  gap: 0;
  width: auto;
}

/* Square on top: the bridge above supplies the group's rounding, so rounding
   these too would leave a visible notch between the two. */
.config-group-tabs button {
  flex: 1;
  padding: 10px 22px;
  font-size: 15px;
  border: 1px solid #d6d6d6;
  border-top: none;
  border-bottom: none;
  border-radius: 0;
  background: #e9ecef;
  color: #444;
  cursor: pointer;
  white-space: nowrap;
}

/* Neighbours share one divider instead of stacking two 1px borders. */
.config-group-tabs button + button {
  margin-left: -1px;
}

/* Same fill as the panel below, so the active tab reads as one surface with
   it rather than as a separate rounded card. Raised so its own left/right
   borders win over the neighbour it overlaps. */
.config-group-tabs button.active {
  background: #ffffffb2;
  color: var(--primary-color);
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.new-main-page-header-text-old {
  height: 40px;
  margin-top: 2%;
  margin-left: 20%;
}
.new-main-page-header-text {
  height: 40px;
  margin-top: 2%;
  margin-left: 20%;
  font-size: 1.5em;
  font-family: "Roboto", sans-serif;
}

.new-main-page-header-text {
  font-size: 1.5em;
  margin-top: 2%;
  margin-left: 0%;
  font-family: "Roboto", sans-serif;
}

.content {
  padding: 20px;
  background-color: #ffffffb2; /* White background */
  border-radius: 20px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Optional: Add a shadow for better visibility */
  margin: 20px; /* Optional: Add margin for spacing */
  max-width: 800px; /* Set a maximum width */
  width: 100%; /* Ensure it takes the full width up to the max-width */
  margin-left: 20px;
  margin-right: auto;
}

h2 {
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.form-group label {
  margin-right: 10px;
  width: 100px; /* Adjust width as needed */
}

.form-group input,
.form-group textarea {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.new-notification-button,
.done-button,
.cancel-button {
  padding: 10px 20px;
  font-size: 16px;
  color: #fff;
  background-color: var(--primary-color);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
}

.new-notification-button:hover,
.done-button:hover,
.cancel-button:hover {
  background-color: #0056b3;
}

.notification-display {
  margin-top: 20px;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay-content {
  position: relative; /* Ensure the close button is positioned relative to the overlay content */
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 400px; /* Set a fixed width for the form */
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.form-header {
  margin-bottom: 10px;
}

.button-container {
  display: flex;
  justify-content: flex-end; /* Align buttons to the right */
  margin-top: 20px;
}

.button-container button {
  margin-left: 10px; /* Add left margin to space out the buttons */
}
@media (max-width: 768px) {
  .content {
    padding: 0px;
    background-color: #e9e5d6; /* White background */
    border-radius: 20px; /* Rounded corners */
    box-shadow: none;
    margin: 20px; /* Optional: Add margin for spacing */
    max-width: 90%; /* Set a maximum width */
    width: 90%; /* Ensure it takes the full width up to the max-width */
    margin-left: 5%; /* Center the content horizontally */
    margin-right: 10%; /* Center the content horizontally */
    margin-top: 25%;
  }

  .overlay-content {
    position: relative; /* Ensure the close button is positioned relative to the overlay content */
    background-color: #fff;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    width: 80vw;
  }

  .new-main-page {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: #e9e5d6;
  }
  .new-main-header {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 5%;
    background-color: #e9e5d6;
    padding: 5;
    margin: 5;
    margin-top: 20px;
  }
  .new-main-page h1,
  .new-main-page p,
  .new-main-page form,
  .new-main-page div {
    width: 100vw;
    text-align: left;
  }
  .header {
    height: auto;
  }
  .header-image {
    width: 100%;
    height: auto;
  }
}
</style>
